import { useState, useMemo } from 'react';
import { useApp } from '../../context/AppContext';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Label } from '../ui/label';
import { calculateCompoundInterest } from '../../utils/calculations';
import { getTaxConfig, INVESTMENT_TYPES, getInvestmentTypeLabels } from '../../utils/taxEngine';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import { GitCompare, TrendingUp, TrendingDown, Minus, Info } from 'lucide-react';

// Build calculation params from stored per-type settings
const buildCalcParams = (settings, typeKey) => {
  const taxConfig = getTaxConfig(typeKey);
  const isAccumLike = typeKey === INVESTMENT_TYPES.ACCUMULATION_PLAN ||
    typeKey === INVESTMENT_TYPES.CHILD_FUTURE;

  return {
    initialDeposit:       settings.initialDeposit        ?? 0,
    monthlyContribution:  settings.monthlyContribution    ?? 0,
    annualInterestRate:   settings.annualInterestRate     ?? 0,
    years:                settings.years                  ?? 20,
    compoundingFrequency: settings.compoundingFrequency   ?? 12,
    contributionFrequency:settings.contributionFrequency  ?? 12,
    taxRate:              (settings.includeTaxes ?? false) ? taxConfig.interestTaxRate : 0,
    taxOnEachPeriod:      taxConfig.taxOnEachPeriod,
    adminFeeRate:         isAccumLike ? (settings.adminFeeRate ?? 1.5) : 0,
    pauseStartYear:       (settings.pauseEnabled && settings.pauseStartYear) || 0,
    pauseEndYear:         (settings.pauseEnabled && settings.pauseEndYear)   || 0,
    useSimpleInterest:    taxConfig.useSimpleInterest,
    topUps:               settings.topUps      || [],
    withdrawals:          settings.withdrawals || [],
  };
};

export const StrategyComparison = () => {
  const { inputs, investmentType, getTypeSettings, t, theme, language, formatMoney } = useApp();
  const isCredit = investmentType === INVESTMENT_TYPES.CREDIT;

  const nonCreditTypes = getInvestmentTypeLabels(language).filter(ty => ty.value !== INVESTMENT_TYPES.CREDIT);

  // Default compare pair: current vs savings (or accum if current is savings)
  const defaultType1 = investmentType !== INVESTMENT_TYPES.CREDIT
    ? investmentType
    : INVESTMENT_TYPES.ACCUMULATION_PLAN;
  const defaultType2 = defaultType1 === INVESTMENT_TYPES.SAVINGS_ACCOUNT
    ? INVESTMENT_TYPES.ACCUMULATION_PLAN
    : INVESTMENT_TYPES.SAVINGS_ACCOUNT;

  const [type1, setType1] = useState(defaultType1);
  const [type2, setType2] = useState(defaultType2);

  // Each strategy uses THAT TYPE's memorized settings
  const results = useMemo(() => {
    const s1 = getTypeSettings(type1);
    const s2 = getTypeSettings(type2);
    const r1 = calculateCompoundInterest(buildCalcParams(s1, type1));
    const r2 = calculateCompoundInterest(buildCalcParams(s2, type2));
    return { r1, r2, s1, s2 };
  }, [getTypeSettings, type1, type2]);

  const chartData = useMemo(() => {
    const len = Math.max(
      results.r1.yearlyBreakdown.length,
      results.r2.yearlyBreakdown.length,
    );
    return Array.from({ length: len }, (_, i) => ({
      year: i + 1,
      strat1: results.r1.yearlyBreakdown[i]?.endBalance ?? results.r1.finalValue,
      strat2: results.r2.yearlyBreakdown[i]?.endBalance ?? results.r2.finalValue,
    }));
  }, [results]);

  const isDark = theme === 'dark';
  if (isCredit) return null;

  const diff = results.r1.finalValue - results.r2.finalValue;
  const absDiff = Math.abs(diff);
  const type1Label = nonCreditTypes.find(ty => ty.value === type1)?.label || '';
  const type2Label = nonCreditTypes.find(ty => ty.value === type2)?.label || '';
  const winner = diff > 0 ? type1Label : diff < 0 ? type2Label : null;

  const s1 = results.s1;
  const s2 = results.s2;

  return (
    <Card className="bg-card border border-border shadow-sm rounded-2xl overflow-hidden" data-testid="strategy-comparison">
      <CardHeader className="pb-4 border-b border-border/50">
        <CardTitle className="flex items-center gap-3 text-xl font-light tracking-tight">
          <div className="p-2 rounded-xl bg-gradient-to-br from-blue-500/10 to-emerald-500/10">
            <GitCompare className="h-5 w-5 text-blue-600 dark:text-blue-400" />
          </div>
          {t('strategyComparison')}
        </CardTitle>
      </CardHeader>

      <CardContent className="p-6 space-y-6">
        {/* Info — each uses its own saved settings */}
        <div className="flex items-start gap-2 p-3 rounded-xl bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 text-xs text-blue-700 dark:text-blue-300">
          <Info className="h-4 w-4 flex-shrink-0 mt-0.5" />
          <span>
            {language === 'ro'
              ? 'Fiecare strategie folosește setările salvate pentru tipul respectiv. Modifică valorile la fiecare tip de instrument și revin automat la comparație.'
              : 'Each strategy uses the saved settings for that type. Modify values for each instrument type and they are automatically remembered here.'}
          </span>
        </div>

        {/* Type selectors */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-[0.12em] text-blue-700 dark:text-blue-300 font-semibold">
              {t('strategy1')}
            </Label>
            <Select value={type1} onValueChange={setType1}>
              <SelectTrigger className="w-full h-11 bg-blue-50 dark:bg-blue-900/30 border-2 border-blue-200 dark:border-blue-700 rounded-xl">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {nonCreditTypes.map(ty => (
                  <SelectItem key={ty.value} value={ty.value}>{ty.icon} {ty.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {/* Mini param summary for s1 */}
            <div className="text-[11px] text-blue-600/70 dark:text-blue-400/70 space-y-0.5 px-1">
              <div>{formatMoney(s1.initialDeposit)} + {formatMoney(s1.monthlyContribution)}/{language === 'ro' ? 'lună' : 'mo'} • {s1.annualInterestRate}% • {s1.years} {language === 'ro' ? 'ani' : 'yr'}</div>
              {(s1.adminFeeRate > 0) && <div>{language === 'ro' ? 'Comision' : 'Fee'}: {s1.adminFeeRate}%/an</div>}
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-[0.12em] text-amber-700 dark:text-amber-300 font-semibold">
              {t('strategy2')}
            </Label>
            <Select value={type2} onValueChange={setType2}>
              <SelectTrigger className="w-full h-11 bg-amber-50 dark:bg-amber-900/30 border-2 border-amber-200 dark:border-amber-700 rounded-xl">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {nonCreditTypes.map(ty => (
                  <SelectItem key={ty.value} value={ty.value}>{ty.icon} {ty.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {/* Mini param summary for s2 */}
            <div className="text-[11px] text-amber-600/70 dark:text-amber-400/70 space-y-0.5 px-1">
              <div>{formatMoney(s2.initialDeposit)} + {formatMoney(s2.monthlyContribution)}/{language === 'ro' ? 'lună' : 'mo'} • {s2.annualInterestRate}% • {s2.years} {language === 'ro' ? 'ani' : 'yr'}</div>
              {(s2.adminFeeRate > 0) && <div>{language === 'ro' ? 'Comision' : 'Fee'}: {s2.adminFeeRate}%/an</div>}
            </div>
          </div>
        </div>

        {/* Result cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="p-5 rounded-2xl border-2 border-blue-300 dark:border-blue-700 bg-blue-50 dark:bg-blue-900/40">
            <div className="text-xs uppercase tracking-[0.12em] text-blue-600 dark:text-blue-400 font-semibold mb-1">{type1Label}</div>
            <div className="text-2xl font-black tracking-tight text-blue-800 dark:text-blue-200 mb-3">
              {formatMoney(results.r1.finalValue)}
            </div>
            <div className="text-xs text-blue-600/80 dark:text-blue-400/80 space-y-1">
              <div className="flex justify-between">
                <span>{language === 'ro' ? 'Contribuții:' : 'Contributions:'}</span>
                <span className="font-semibold">{formatMoney(results.r1.totalContributions)}</span>
              </div>
              <div className="flex justify-between">
                <span>{language === 'ro' ? 'Câștig net:' : 'Net gain:'}</span>
                <span className="font-semibold">{formatMoney(results.r1.totalInterestNet)}</span>
              </div>
              {results.r1.totalAdminFees > 0 && (
                <div className="flex justify-between">
                  <span>{language === 'ro' ? 'Comisioane:' : 'Fees:'}</span>
                  <span className="font-semibold">{formatMoney(results.r1.totalAdminFees)}</span>
                </div>
              )}
              {results.r1.totalTaxPaid > 0 && (
                <div className="flex justify-between">
                  <span>{language === 'ro' ? 'Impozit:' : 'Tax:'}</span>
                  <span className="font-semibold">{formatMoney(results.r1.totalTaxPaid)}</span>
                </div>
              )}
            </div>
          </div>

          <div className="p-5 rounded-2xl border-2 border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/40">
            <div className="text-xs uppercase tracking-[0.12em] text-amber-600 dark:text-amber-400 font-semibold mb-1">{type2Label}</div>
            <div className="text-2xl font-black tracking-tight text-amber-800 dark:text-amber-200 mb-3">
              {formatMoney(results.r2.finalValue)}
            </div>
            <div className="text-xs text-amber-600/80 dark:text-amber-400/80 space-y-1">
              <div className="flex justify-between">
                <span>{language === 'ro' ? 'Contribuții:' : 'Contributions:'}</span>
                <span className="font-semibold">{formatMoney(results.r2.totalContributions)}</span>
              </div>
              <div className="flex justify-between">
                <span>{language === 'ro' ? 'Câștig net:' : 'Net gain:'}</span>
                <span className="font-semibold">{formatMoney(results.r2.totalInterestNet)}</span>
              </div>
              {results.r2.totalAdminFees > 0 && (
                <div className="flex justify-between">
                  <span>{language === 'ro' ? 'Comisioane:' : 'Fees:'}</span>
                  <span className="font-semibold">{formatMoney(results.r2.totalAdminFees)}</span>
                </div>
              )}
              {results.r2.totalTaxPaid > 0 && (
                <div className="flex justify-between">
                  <span>{language === 'ro' ? 'Impozit:' : 'Tax:'}</span>
                  <span className="font-semibold">{formatMoney(results.r2.totalTaxPaid)}</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Difference banner */}
        <div className={`p-4 rounded-xl flex items-center gap-3 font-semibold text-sm
          ${diff > 100 ? 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-800 dark:text-emerald-200'
            : diff < -100 ? 'bg-red-100 dark:bg-red-900/40 text-red-800 dark:text-red-200'
            : 'bg-muted text-muted-foreground'}`}>
          {diff > 100 ? <TrendingUp className="h-5 w-5 flex-shrink-0" />
            : diff < -100 ? <TrendingDown className="h-5 w-5 flex-shrink-0" />
            : <Minus className="h-5 w-5 flex-shrink-0" />}
          <span>
            {Math.abs(diff) <= 100
              ? (language === 'ro' ? 'Rezultate aproape identice' : 'Nearly identical results')
              : language === 'ro'
                ? `${winner} produce cu ${formatMoney(absDiff)} mai mult după ${Math.max(s1.years, s2.years)} ani`
                : `${winner} yields ${formatMoney(absDiff)} more after ${Math.max(s1.years, s2.years)} years`}
          </span>
        </div>

        {/* Chart */}
        <div className="h-[260px] w-full">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="cg1" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.35} />
                  <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="cg2" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.35} />
                  <stop offset="95%" stopColor="#f59e0b" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#27272a' : '#e2e8f0'} vertical={false} />
              <XAxis dataKey="year" stroke={isDark ? '#71717a' : '#94a3b8'} fontSize={11} tickLine={false} axisLine={false}
                label={{ value: language === 'ro' ? 'Ani' : 'Years', position: 'insideBottom', offset: -2, fontSize: 10 }} />
              <YAxis stroke={isDark ? '#71717a' : '#94a3b8'} fontSize={11} tickLine={false} axisLine={false}
                tickFormatter={(v) => v >= 1000000 ? `${(v/1000000).toFixed(1)}M` : `${(v/1000).toFixed(0)}K`}
                width={56} />
              <Tooltip
                formatter={(v, name) => [formatMoney(v), name]}
                labelFormatter={(l) => `${language === 'ro' ? 'An' : 'Year'} ${l}`}
                contentStyle={{
                  backgroundColor: isDark ? '#18181b' : '#fff',
                  border: `1px solid ${isDark ? '#27272a' : '#e2e8f0'}`,
                  borderRadius: '12px', padding: '10px',
                }}
              />
              <Legend wrapperStyle={{ paddingTop: '8px', fontSize: '12px' }} />
              <Area type="monotone" dataKey="strat1" name={type1Label}
                stroke="#3b82f6" strokeWidth={2.5} fill="url(#cg1)" animationDuration={800} />
              <Area type="monotone" dataKey="strat2" name={type2Label}
                stroke="#f59e0b" strokeWidth={2.5} fill="url(#cg2)" animationDuration={800} animationBegin={150} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
};

export default StrategyComparison;
